import { ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import logo from "@/assets/logo-header.webp";
import { LenderLogo } from "./LenderLogo";
import { type Lender } from "@/lib/lenders";
import { useApplyUrl } from "@/lib/epi";

function formatKr(n: number) {
  return new Intl.NumberFormat("sv-SE").format(n) + " kr";
}

export function StickyMobileBar({
  lender,
  product = "lånet",
}: {
  lender: Lender;
  product?: string;
}) {
  const apply = useApplyUrl();
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card/95 p-3 shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.15)] backdrop-blur-lg lg:hidden">
      <div className="mx-auto flex max-w-lg items-center gap-3">
        <LenderLogo lender={lender} size={44} bare />
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
            Toppval {product}
          </div>
          <div className="truncate font-display text-sm font-extrabold text-foreground">
            {lender.name}
          </div>
          <div className="truncate text-[11px] font-semibold text-foreground/70">
            {formatKr(lender.minAmount)} – {formatKr(lender.maxAmount)} ·{" "}
            <span className="text-primary">{lender.rate}</span>
          </div>
        </div>
        <a
          href={apply(lender, "sticky-mobile")}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-cta px-4 py-2.5 text-sm font-bold text-cta-foreground shadow-md"
        >
          Ansök nu <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    </div>
  );
}

const productLinks = [
  { label: "Privatlån", to: "/privatlan" },
  { label: "Billån", to: "/billan" },
  { label: "Samla lån", to: "/samla-lan" },
  { label: "Snabblån", to: "/snabblan" },
  { label: "SMS-lån", to: "/smslan" },
  { label: "Lån utan UC", to: "/lan-utan-uc" },
] as const;

const guideLinks = [
  { label: "Lånekalkyl", to: "/guider/lanekalkyl" },
  { label: "Ordlista", to: "/ordlista" },
  { label: "Nyheter", to: "/nyhet" },
] as const;

const pseoAmountLinks = [
  { label: "Låna 20 000 kr", to: "/lan/20000-kr" },
  { label: "Låna 50 000 kr", to: "/lan/50000-kr" },
  { label: "Låna 100 000 kr", to: "/lan/100000-kr" },
  { label: "Låna 250 000 kr", to: "/lan/250000-kr" },
] as const;

const pseoTypeLinks = [
  { label: "Låna 50 000 kr utan UC", to: "/lan-utan-uc/50000-kr" },
  { label: "Lån 30 000 kr med betalningsanmärkning", to: "/lan-med-betalningsanmarkning/30000-kr" },
  { label: "Samla lån 150 000 kr", to: "/samla-lan/150000-kr" },
  { label: "Snabblån 25 000 kr", to: "/snabblan/25000-kr" },
] as const;

const serviceLinks = [
  { label: "Snabblån från 1000 kr", to: "/snabblan-1000-kr" },
  { label: "Akut SMS-lån från 1000 kr via Swish", to: "/sms-lan-1000-kr-swish" },
  { label: "Mikrolån utan UC från 1000 kr", to: "/mikrolan-utan-uc-1000-kr" },
] as const;

const aboutLinks = [
  { label: "Vår redaktion", to: "/om-oss/redaktion" },
  { label: "Hur vi granskar långivare", to: "/om-oss/hur-vi-granskar" },
  { label: "Kontakt", to: "/kontakt" },
  { label: "Allmänna villkor", to: "/villkor" },
  { label: "Integritetspolicy", to: "/integritetspolicy" },
  { label: "Cookies", to: "/cookies" },
] as const;

function LinkColumn({
  title,
  links,
}: {
  title: string;
  links: readonly { label: string; to: string }[];
}) {
  return (
    <div>
      <div className="font-display text-sm font-bold uppercase tracking-wider text-primary">
        {title}
      </div>
      <ul className="mt-4 space-y-2.5 text-sm text-foreground/80">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="transition-colors hover:text-primary hover:underline">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="mt-16 border-t border-border bg-surface pb-24 pt-14 text-foreground lg:pb-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <img
          src={logo}
          alt="Svensk Låneguide"
          width={290}
          height={100}
          loading="lazy"
          decoding="async"
          className="h-10 w-auto"
        />

        <div className="mt-8 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          <LinkColumn title="Låneprodukter" links={productLinks} />
          <LinkColumn title="Guider" links={guideLinks} />
          <LinkColumn title="Om oss" links={aboutLinks} />
        </div>

        <div className="mt-10 border-t border-border pt-6">
          <div className="font-display text-sm font-bold uppercase tracking-wider text-primary">
            Populära lånesökningar
          </div>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground/80">
            {pseoAmountLinks.map((l) => (
              <a key={l.to} href={l.to} className="transition-colors hover:text-primary hover:underline">
                {l.label}
              </a>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground/80">
            {pseoTypeLinks.map((l) => (
              <a key={l.to} href={l.to} className="transition-colors hover:text-primary hover:underline">
                {l.label}
              </a>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-foreground/80">
            {serviceLinks.map((l) => (
              <a key={l.to} href={l.to} className="transition-colors hover:text-primary hover:underline">
                {l.label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-10 border-t border-border pt-6 text-[11px] leading-relaxed text-foreground/75">
          Svensk Låneguide är inte en kortutgivare och erbjuder inte någon form av finansiell rådgivning. Vi står inte under Finansinspektionens tillsyn och ger inga rekommendationer kring finansiella beslut. Svensk Låneguide är en oberoende jämförelsetjänst, och har inte någon direkt anknytning till en enskild bank eller annat finansiellt institut. Vi samarbetar dock med ett antal långivare och kan komma att erhålla ekonomisk ersättning genom annonslänkar på sajten.
        </div>

        <div className="mt-4 border-t border-border pt-3 text-[11px] leading-relaxed text-foreground/75">
          Marknadsföring &amp; Hemsida skapad av{" "}
          <a
            href="https://adtract.se"
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 transition-colors hover:text-primary"
          >
            AdTract
          </a>
        </div>
      </div>
    </footer>
  );
}
